import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { DollarSign, RefreshCw, CheckCircle, AlertCircle, Loader, Users, FileText, Package, TrendingUp, Link as LinkIcon } from 'lucide-react';
import { toast } from 'sonner';

export default function QuickBooksOnline() {
  const [connected, setConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncStats, setSyncStats] = useState(null);

  useEffect(() => {
    checkConnection();
  }, []);

  const checkConnection = async () => {
    try {
      const response = await api.get('/quickbooks/status');
      setConnected(response.data.connected);
      if (response.data.stats) {
        setSyncStats(response.data.stats);
      }
    } catch (error) {
      setConnected(false);
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async () => {
    try {
      const response = await api.get('/quickbooks/auth/url');
      window.location.href = response.data.authorization_url;
    } catch (error) {
      toast.error('Failed to initiate QuickBooks connection');
    }
  };

  const handleSync = async (dataType) => {
    setSyncing(true);
    try {
      await api.post(`/quickbooks/sync/${dataType}`);
      toast.success(`Synced successfully`);
      checkConnection();
    } catch (error) {
      toast.error('Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <DollarSign className="w-8 h-8 text-green-600" />
            QuickBooks Online
          </h1>
          <p className="text-gray-600 mt-1">Sync financial data with QuickBooks</p>
        </div>
        <Badge 
          variant="custom"
          className={connected ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}
        >
          {connected ? 'Connected' : 'Not Connected'}
        </Badge>
      </div>

      {!connected ? (
        <Card>
          <CardHeader>
            <CardTitle>Connect QuickBooks Online</CardTitle>
            <CardDescription>Authorize CipherOne to access your QuickBooks data</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert className="bg-blue-50 border-blue-200">
              <AlertCircle className="h-4 w-4 text-blue-600" />
              <AlertDescription className="text-blue-900">
                You'll be redirected to QuickBooks to authorize access.
              </AlertDescription>
            </Alert>
            <Button onClick={handleConnect} className="w-full" size="lg">
              <LinkIcon className="h-4 w-4 mr-2" />
              Connect QuickBooks Online
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Customers</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold">{syncStats?.customers || 0}</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Invoices</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold">{syncStats?.invoices || 0}</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Products</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold">{syncStats?.products || 0}</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Payments</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold">{syncStats?.payments || 0}</p>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Sync Data</CardTitle>
              <CardDescription>Sync data from QuickBooks Online</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Button onClick={() => handleSync('customers')} disabled={syncing} variant="outline">
                  <Users className="h-4 w-4 mr-2" />
                  Sync Customers
                </Button>
                <Button onClick={() => handleSync('invoices')} disabled={syncing} variant="outline">
                  <FileText className="h-4 w-4 mr-2" />
                  Sync Invoices
                </Button>
                <Button onClick={() => handleSync('products')} disabled={syncing} variant="outline">
                  <Package className="h-4 w-4 mr-2" />
                  Sync Products
                </Button>
                <Button onClick={() => handleSync('payments')} disabled={syncing} variant="outline">
                  <TrendingUp className="h-4 w-4 mr-2" />
                  Sync Payments
                </Button>
              </div>
              <div className="pt-4 border-t mt-4">
                <Button onClick={() => handleSync('all')} disabled={syncing} className="w-full">
                  {syncing ? (
                    <>
                      <Loader className="h-4 w-4 mr-2 animate-spin" />
                      Syncing...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Sync All Data
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      <Alert className="bg-green-50 border-green-200">
        <CheckCircle className="h-4 w-4 text-green-600" />
        <AlertDescription className="text-green-900">
          <strong>QuickBooks Online Integration:</strong> Automatically sync customers, invoices, products, and payments. Keep your financial data in sync between CipherOne and QuickBooks.
        </AlertDescription>
      </Alert>
    </div>
  );
}
